<script lang="ts" module>
  // Button — the design-system text button (wiki/feature-design-system.md
  // §Component primitives, AC4). `danger` fills with the theme-independent
  // recording-red, never the practice accent, so a destructive control always
  // reads red. `link` is the text-style action — no fill, still a real control
  // with a focus ring. `soft` is the emphasis between `primary` and `ghost`: an
  // accent wash for the one action a card wants noticed without shouting.
  // `plain` is a label-only toggle in a strip of settings: no frame, and muted
  // while `aria-pressed` is false. `round` draws the pill a suggestion chip
  // wears. `href` renders an `<a>` wearing the same chrome.
  //
  // Icons ride in as TYPED COMPONENT PROPS (`icon={Pencil}`), never a slot, and
  // are decorative: the `children` label carries the accessible name. Dropping
  // the label is allowed ONLY together with an icon AND an explicit
  // `aria-label` — the `ButtonProps` union below is what makes a silently
  // nameless button a type error.
  import type { LucideIcon } from '$lib/ui/icons';

  export type ButtonVariant = 'primary' | 'commerce' | 'soft' | 'ghost' | 'danger' | 'link' | 'plain';
  export type ButtonSize = 'sm' | 'md';
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';
  import { resolveHref } from '$lib/utils/routing/resolveHref';
  import type { PathnameWithSearchOrHash } from '$app/types';

  interface BaseProps {
    /** `primary` = accent fill, `ghost` = hairline (default `ghost`). */
    variant?: ButtonVariant;
    /** `md` (default) or the compact `sm` used in a take's action row. */
    size?: ButtonSize;
    /** Fully rounded ends — a pill — instead of the control radius. */
    round?: boolean;
    /** Leading icon (a Lucide component), decorative. */
    icon?: LucideIcon;
    /** Trailing icon (a Lucide component), decorative. */
    iconTrailing?: LucideIcon;
    /** Native button type (default `button`). */
    type?: 'button' | 'submit' | 'reset';
    /**
     * Render an `<a href>` wearing the button chrome. Internal routes only —
     * it runs through `resolve()` so the `no-navigation-without-resolve`
     * contract holds; an external CTA uses a plain `<a rel="external">`.
     */
    href?: PathnameWithSearchOrHash;
    /** Anchor target (only meaningful with `href`). */
    target?: string;
    /** Anchor rel (only meaningful with `href`). */
    rel?: string;
    /** Disabled state. */
    disabled?: boolean;
    /** Click handler. */
    onclick?: (event: MouseEvent) => void;
    /** Accessible label, when the visible content is non-textual. */
    'aria-label'?: string;
    /** Reflects a toggle state for toggle buttons. */
    'aria-pressed'?: boolean;
    /** Reflects whether the control's disclosure target is expanded. */
    'aria-expanded'?: boolean;
    /** Id of the element describing this control (a tooltip / help line). */
    'aria-describedby'?: string;
    'aria-keyshortcuts'?: string;
    /** Native title tooltip. */
    title?: string;
    /** Test hook. */
    'data-testid'?: string;
    /** Extra classes. */
    class?: string;
    /** Button label content. */
    children?: Snippet;
  }

  type Props = BaseProps &
    ({ children: Snippet } | { icon: LucideIcon; 'aria-label': string });

  let {
    variant = 'ghost',
    size = 'md',
    round = false,
    icon: Icon,
    iconTrailing: IconTrailing,
    type = 'button',
    href,
    target,
    rel,
    disabled = false,
    onclick,
    title,
    children,
    class: klass = '',
    'aria-label': ariaLabel,
    'aria-pressed': ariaPressed,
    'aria-expanded': ariaExpanded,
    'aria-describedby': ariaDescribedby,
    'aria-keyshortcuts': ariaKeyshortcuts,
    'data-testid': dataTestid,
  }: Props = $props();

  const iconPx = $derived(size === 'sm' ? 15 : 18);
  // The gap rides a class, not the base rule, so a label-only button renders
  // byte-identically to the pre-icon Button whatever its children contain.
  const classes = $derived(
    `vd-btn vd-btn--${variant} vd-btn--${size}` +
      (Icon || IconTrailing ? ' vd-btn--iconed' : '') +
      (round ? ' vd-btn--round' : '') +
      (klass ? ` ${klass}` : ''),
  );
</script>

{#snippet inner()}
  {#if Icon}
    <Icon size={iconPx} strokeWidth={1.75} aria-hidden="true" focusable="false" />
  {/if}
  {@render children?.()}
  {#if IconTrailing}
    <IconTrailing size={iconPx} strokeWidth={1.75} aria-hidden="true" focusable="false" />
  {/if}
{/snippet}

{#if href}
  <!-- A disabled link has no native equivalent, so drop the href and mark it
       aria-disabled rather than firing navigation. -->
  <a
    href={disabled || href === undefined ? undefined : resolveHref(href)}
    {target}
    {rel}
    {title}
    class={classes}
    class:vd-btn--disabled={disabled}
    aria-label={ariaLabel}
    aria-describedby={ariaDescribedby}
    aria-disabled={disabled ? true : undefined}
    data-testid={dataTestid}
    {onclick}
  >
    {@render inner()}
  </a>
{:else}
  <button
    {type}
    {disabled}
    {onclick}
    {title}
    class={classes}
    aria-label={ariaLabel}
    aria-pressed={ariaPressed}
    aria-expanded={ariaExpanded}
    aria-describedby={ariaDescribedby}
    aria-keyshortcuts={ariaKeyshortcuts}
    data-testid={dataTestid}
  >
    {@render inner()}
  </button>
{/if}

<style lang="scss">
  .vd-btn {
    // `inline-flex` so the `<a>` rendering centres its label like the
    // `<button>` does (and an anchor never carries underline chrome).
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-control);
    line-height: 1;
    border: 1px solid var(--line);
    background: transparent;
    color: var(--ink);
    cursor: pointer;
    text-decoration: none;
    transition:
      border-color 0.2s ease,
      filter 0.2s ease,
      background 0.2s ease;

    &:hover:not(:disabled):not(.vd-btn--disabled) {
      border-color: var(--accent);
    }

    &:disabled,
    &.vd-btn--disabled {
      cursor: not-allowed;
      opacity: 0.55;
      pointer-events: none;
    }

    &:focus-visible {
      outline: 2px solid var(--accent-text);
      outline-offset: 2px;
    }
  }

  .vd-btn--md {
    font-size: var(--text-control);
    padding: var(--control-pad-top) var(--control-pad-x) var(--control-pad-bottom);
    border-radius: var(--control-radius);
  }

  // The compact action-row size — a take's Download / AI Review / Save row.
  .vd-btn--sm {
    font-size: var(--text-control-sm);
    padding: var(--control-pad-y-sm) var(--control-pad-x-sm);
    // The small toggle's button radius, its nearest sibling.
    border-radius: calc(var(--control-radius-sm) - var(--control-track-sm));
    white-space: nowrap;
  }

  .vd-btn--iconed {
    gap: 0.45rem;
  }

  .vd-btn--primary {
    background: var(--accent);
    color: var(--on-accent);
    border-color: var(--accent);

    &:hover:not(:disabled):not(.vd-btn--disabled) {
      border-color: var(--accent);
      filter: brightness(1.08);
    }
  }

  // Billing / upgrade CTA — the fixed commerce accent, so the button that takes
  // money never inherits a muted page accent.
  .vd-btn--commerce {
    background: var(--accent-commerce);
    color: var(--on-fill);
    border-color: var(--accent-commerce);

    &:hover:not(:disabled):not(.vd-btn--disabled) {
      border-color: var(--accent-commerce);
      filter: brightness(1.08);
    }

    &:focus-visible {
      outline-color: var(--accent-commerce);
    }
  }

  .vd-btn--soft {
    border-color: color-mix(in srgb, var(--accent) 55%, var(--line));
    background: color-mix(in srgb, var(--accent) 10%, var(--paper));
    font-weight: 700;
  }

  // Destructive action — the semantic recording-red (theme-independent), never
  // the practice accent, so a destructive control always reads red. `md` FILLS:
  // it is the dialog confirm ("End session", "Clean up") that should shout.
  // `sm` OUTLINES — see the rule below. Focus outline follows the same red so
  // it doesn't clash with the fill.
  .vd-btn--danger {
    background: var(--color-recording);
    color: var(--on-fill);
    border-color: var(--color-recording);

    &:hover:not(:disabled):not(.vd-btn--disabled) {
      border-color: var(--color-recording);
      filter: brightness(1.08);
    }

    &:focus-visible {
      outline-color: var(--color-recording);
    }
  }

  // "A destructive control that is one of a pair of small affordances should read
  // as red, not shout" — IconButton's danger tone, same reasoning. The asymmetry
  // with the filled `md` is DELIBERATE: the two sizes ARE the two emphases,
  // which is why there is no tone axis. Do not "fix" it.
  //
  // `--paper`, not transparent: the dark label is exactly 4.50 on paper, so a
  // tinted ground behind it (the card is a 5% accent wash) reads 4.31 — under AA.
  .vd-btn--danger.vd-btn--sm {
    background: var(--paper);
    color: var(--color-recording-text);
    border-color: color-mix(in srgb, var(--color-recording) 30%, var(--line));

    // Border only: IconButton's 8% hover wash is fine under a glyph (3.0 floor)
    // but reads 4.48 / 4.25 under this label.
    &:hover:not(:disabled):not(.vd-btn--disabled) {
      filter: none;
      border-color: var(--color-recording);
    }
  }

  // Text-style action — no fill, no border box. It keeps the underline so it is
  // never mistaken for body text, and the base focus ring still draws (padded
  // out, since the box collapses to the label). Declared after the size rules
  // because it overrides their padding and radius.
  .vd-btn--link {
    padding: 0;
    border-color: transparent;
    color: var(--accent-text);
    text-decoration: underline;
    text-underline-offset: 3px;
    border-radius: 4px;

    &:hover:not(:disabled):not(.vd-btn--disabled) {
      border-color: transparent;
      text-decoration-thickness: 2px;
    }

    &:focus-visible {
      outline-offset: 3px;
    }
  }

  // Its colours are `--ink` / `--ink-mute`, never fixed values, so a strip that
  // reslots those tokens (the over-video edge strips) recolours it. Declared
  // after the size rules because it overrides their padding and radius.
  .vd-btn--plain {
    padding: 0.45rem 0.4rem;
    border-color: transparent;
    border-radius: 4px;

    &[aria-pressed='false'] {
      color: var(--ink-mute);
    }

    &:hover:not(:disabled):not(.vd-btn--disabled) {
      border-color: transparent;
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  .vd-btn--round {
    border-radius: 999px;
  }

  // Touch sizing is a function of INPUT, not width (WCAG 2.5.8) — matched to the
  // 44px every stage control clears. `link` is exempt: it is a text action inside
  // a line of prose, where a 44px box opens a gap in the paragraph.
  @media (pointer: coarse) {
    .vd-btn:not(.vd-btn--link) {
      min-height: 44px;
    }
  }
</style>
